import type { JSONContent } from "@tiptap/core";
import { Fragment, type ReactNode } from "react";

import { cn } from "@/lib/classNames";
import { normalizeNoteContent, extractPlainTextFromNoteContent } from "./noteContent";

interface NoteRichPreviewProps {
  content: unknown;
  className?: string;
  maxBlocks?: number;
}

const DEFAULT_MAX_BLOCKS = 8;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function applyMarks(node: ReactNode, marks: JSONContent["marks"]): ReactNode {
  if (!marks || marks.length === 0) return node;

  return marks.reduce<ReactNode>((acc, mark) => {
    switch (mark.type) {
      case "bold":
        return <strong>{acc}</strong>;
      case "italic":
        return <em>{acc}</em>;
      case "underline":
        return <u>{acc}</u>;
      case "strike":
        return <s>{acc}</s>;
      case "code":
        return (
          <code className="rounded bg-neutral-950/10 px-1 py-px font-mono text-[0.9em]">
            {acc}
          </code>
        );
      case "link": {
        const href =
          isRecord(mark.attrs) && typeof mark.attrs.href === "string"
            ? mark.attrs.href
            : "#";
        return (
          <a
            href={href}
            target="_blank"
            rel="noreferrer noopener"
            className="underline decoration-1 underline-offset-2"
          >
            {acc}
          </a>
        );
      }
      default:
        return acc;
    }
  }, node);
}

function renderInline(nodes: JSONContent[] | undefined, keyPrefix: string): ReactNode[] {
  if (!nodes) return [];

  return nodes.map((node, index) => {
    const key = `${keyPrefix}.${index}`;

    if (node.type === "hardBreak") {
      return <br key={key} />;
    }

    if (node.type === "text") {
      const text = typeof node.text === "string" ? node.text : "";
      return <Fragment key={key}>{applyMarks(text, node.marks)}</Fragment>;
    }

    if (node.type === "subNote") {
      const noteTitle =
        isRecord(node.attrs) && typeof node.attrs.noteTitle === "string"
          ? node.attrs.noteTitle
          : "Untitled";
      return (
        <span
          key={key}
          className="inline-flex items-center gap-1 rounded bg-neutral-950/8 px-1.5 py-px text-[0.95em] italic"
        >
          {noteTitle}
        </span>
      );
    }

    return <Fragment key={key}>{renderInline(node.content, key)}</Fragment>;
  });
}

function renderBlock(node: JSONContent, key: string): ReactNode {
  switch (node.type) {
    case "paragraph": {
      const children = renderInline(node.content, key);
      return (
        <p key={key} className="m-0 whitespace-pre-wrap">
          {children.length > 0 ? children : <span>&nbsp;</span>}
        </p>
      );
    }
    case "heading": {
      const level =
        isRecord(node.attrs) && typeof node.attrs.level === "number"
          ? node.attrs.level
          : 2;
      const children = renderInline(node.content, key);
      const classes =
        "m-0 font-normal leading-snug font-['Fondamento','Palatino_Linotype',serif]";
      switch (level) {
        case 1:
          return (
            <h3 key={key} className={cn(classes, "text-[1.35em]")}>
              {children}
            </h3>
          );
        case 2:
          return (
            <h4 key={key} className={cn(classes, "text-[1.2em]")}>
              {children}
            </h4>
          );
        default:
          return (
            <h5 key={key} className={cn(classes, "text-[1.1em]")}>
              {children}
            </h5>
          );
      }
    }
    case "bulletList": {
      const items = (node.content ?? []).map((item, i) => (
        <li key={`${key}.li.${i}`}>{renderBlocks(item.content, `${key}.li.${i}`)}</li>
      ));
      return (
        <ul key={key} className="m-0 list-disc pl-5">
          {items}
        </ul>
      );
    }
    case "orderedList": {
      const items = (node.content ?? []).map((item, i) => (
        <li key={`${key}.li.${i}`}>{renderBlocks(item.content, `${key}.li.${i}`)}</li>
      ));
      return (
        <ol key={key} className="m-0 list-decimal pl-5">
          {items}
        </ol>
      );
    }
    case "blockquote":
      return (
        <blockquote
          key={key}
          className="m-0 border-l-2 border-neutral-950/25 pl-3 italic"
        >
          {renderBlocks(node.content, key)}
        </blockquote>
      );
    case "codeBlock": {
      const text = (node.content ?? [])
        .map((child) => (typeof child.text === "string" ? child.text : ""))
        .join("");
      return (
        <pre
          key={key}
          className="m-0 overflow-x-auto rounded bg-neutral-950/8 p-2 font-mono text-[0.85em]"
        >
          <code>{text}</code>
        </pre>
      );
    }
    case "horizontalRule":
      return <hr key={key} className="my-1 border-t border-neutral-950/15" />;
    case "subNote": {
      const noteTitle =
        isRecord(node.attrs) && typeof node.attrs.noteTitle === "string"
          ? node.attrs.noteTitle
          : "Untitled";
      return (
        <p key={key} className="m-0 italic text-neutral-700">
          {noteTitle}
        </p>
      );
    }
    case "callout":
    case "toggle":
      return (
        <div key={key} className="m-0">
          {renderBlocks(node.content, key)}
        </div>
      );
    default:
      return null;
  }
}

function renderBlocks(nodes: JSONContent[] | undefined, keyPrefix: string): ReactNode[] {
  if (!nodes) return [];
  return nodes
    .map((node, index) => renderBlock(node, `${keyPrefix}.${index}`))
    .filter((node): node is ReactNode => node !== null);
}

export function NoteRichPreview({ content, className, maxBlocks = DEFAULT_MAX_BLOCKS }: NoteRichPreviewProps) {
  const document = normalizeNoteContent(content);
  const blocks = Array.isArray(document.content) ? document.content : [];

  const limited = blocks.slice(0, maxBlocks);
  const truncated = blocks.length > maxBlocks;
  const rendered = renderBlocks(limited, "blk");

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      {rendered}
      {truncated ? <p className="m-0 text-neutral-600">...</p> : null}
    </div>
  );
}

export function noteContentIsEmpty(content: unknown): boolean {
  return extractPlainTextFromNoteContent(content).length === 0;
}
